import { memo } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Panel,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const COLORS = {
  root: '#0a104f',
  vda: '#1a237e',
  geneticVariation: '#7c3aed',
} as const;

type HandleDef = {
  id: string;
  type: 'source' | 'target';
  pos: 'left' | 'right' | 'top' | 'bottom';
};

interface OntologyNodeData extends Record<string, unknown> {
  title: string;
  headerColor: string;
  borderColor: string;
  handles: HandleDef[];
}

type OntologyEntityNode = Node<OntologyNodeData, 'ontologyEntity'>;

const POS_MAP: Record<string, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

const OntologyEntityNodeView = memo(function OntologyEntityNodeView({
  id,
  data,
}: NodeProps<OntologyEntityNode>) {
  const { title, headerColor, borderColor, handles } = data;
  return (
    <div
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: 10,
        background: '#fff',
        width: 190,
        boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {handles.map(h => (
        <Handle
          key={h.id}
          id={h.id}
          type={h.type}
          position={POS_MAP[h.pos]}
          style={{ background: borderColor, width: 9, height: 9, border: '2px solid #fff' }}
          aria-label={`${id}-${h.id}`}
        />
      ))}
      <div style={{ background: headerColor, padding: '10px 12px' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.3,
            textAlign: 'center',
            whiteSpace: 'normal',
          }}
        >
          {title}
        </div>
      </div>
      <div style={{ height: 8 }} />
    </div>
  );
});

const NODE_TYPES: NodeTypes = {
  ontologyEntity: OntologyEntityNodeView as NodeTypes[string],
};

function h(nodeId: string, which: 'top' | 'bottom') {
  return `${nodeId}-${which}`;
}

function node(id: string, x: number, y: number, title: string, color: string): OntologyEntityNode {
  return {
    id,
    type: 'ontologyEntity',
    position: { x, y },
    data: {
      title,
      headerColor: color,
      borderColor: color,
      handles: [
        { id: h(id, 'top'), type: 'target', pos: 'top' },
        { id: h(id, 'bottom'), type: 'source', pos: 'bottom' },
      ],
    },
  };
}

function edge(id: string, source: string, target: string, color: string): Edge {
  return {
    id,
    source,
    target,
    sourceHandle: h(source, 'bottom'),
    targetHandle: h(target, 'top'),
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    style: { stroke: color, strokeWidth: 2 },
  };
}

const C = COLORS;

const NODES: OntologyEntityNode[] = [
  node('association', 480, 0, 'Association', C.root),
  node('vda', 480, 140, 'Variant Disease Association', C.vda),
  node('genetic_variation', 480, 280, 'Genetic Variation', C.geneticVariation),

  node('drug_response_variant', 40, 440, 'Drug Response Variant', C.geneticVariation),
  node('susceptibility_mutation', 280, 440, 'Susceptibility Mutation', C.geneticVariation),
  node('casual_mutation', 520, 440, 'Casual Mutation', C.geneticVariation),
  node('protective_variant', 760, 440, 'Protective Variant', C.geneticVariation),
  node('vus', 1000, 440, 'VUS', C.geneticVariation),

  node('pharmacogenomics', 40, 600, 'Pharmacogenomics', C.geneticVariation),
];

const EDGES: Edge[] = [
  edge('e-assoc-vda', 'association', 'vda', C.vda),
  edge('e-vda-gv', 'vda', 'genetic_variation', C.geneticVariation),

  edge('e-gv-drug', 'genetic_variation', 'drug_response_variant', C.geneticVariation),
  edge('e-gv-sus', 'genetic_variation', 'susceptibility_mutation', C.geneticVariation),
  edge('e-gv-casual', 'genetic_variation', 'casual_mutation', C.geneticVariation),
  edge('e-gv-protective', 'genetic_variation', 'protective_variant', C.geneticVariation),
  edge('e-gv-vus', 'genetic_variation', 'vus', C.geneticVariation),

  edge('e-drug-pharma', 'drug_response_variant', 'pharmacogenomics', C.geneticVariation),
];

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: '#444', fontFamily: 'Arial, sans-serif' }}>{label}</span>
    </div>
  );
}

export default function DISGENETOntologyVDAFlow() {
  return (
    <div
      style={{
        width: '100%',
        height: 760,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #e0e8f0',
      }}
    >
      <ReactFlow
        nodes={NODES}
        edges={EDGES}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        proOptions={{ hideAttribution: true }}
        style={{ background: '#f8fafc' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dde4ed" />
        <Controls style={{ background: 'transparent', border: 'none', boxShadow: 'none', gap: 4 }} />
        <Panel position="top-left">
          <div
            style={{
              background: 'rgba(255,255,255,0.94)',
              border: '1px solid #e0e8f0',
              borderRadius: 8,
              padding: '9px 14px',
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#999',
                marginBottom: 7,
              }}
            >
              Legend
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <LegendItem color={C.vda} label="Variant Disease Association" />
              <LegendItem color={C.geneticVariation} label="Genetic Variation branch" />
            </div>
          </div>
        </Panel>
      </ReactFlow>
      <style>{`
        .react-flow__controls-button {
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.14) !important;
          border: 1px solid #e0e8f0 !important;
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .react-flow__controls-button:hover { background: #f0f7ff !important; }
        .react-flow__controls-button svg { max-width: 12px; max-height: 12px; }
      `}</style>
    </div>
  );
}
